import { fullNameOf, initialsOf } from '@/utils/personName.ts';
import { StatusTone } from '@/components/ui/statusTone.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import { PersonTargetType, TargetType } from '@/types/TargetType.ts';
import {
  BillRowAction,
  PersonIdentityVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { ChangePackageStatus } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';

/**
 * Etykiety i tony odznak dla enumów modelu widoku rachunku.
 *
 * Jedyne miejsce, w którym te napisy istnieją — komponenty nigdy nie budują
 * własnych map statusów (reguła z `CLAUDE.md`). Napisy odpowiadają kopii
 * z projektu graficznego, a tony biorą się ze wspólnego słownika
 * `components/ui/statusTone.ts`.
 */

/**
 * Statusy rezerwacji w brzmieniu z projektu.
 *
 * Enum jest wspólny (`features/public/bookingSummary`), ale napisy nie: strona
 * publiczna mówi klientowi „Aktywna”, a odznaka nagłówka ekranu zmian —
 * „Potwierdzona”, bo operator patrzy na rezerwację od strony potwierdzenia
 * zamówienia. Rozjazd jest świadomy i opisany w MISSING_ENDPOINTS.md.
 */
export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  [BookingStatus.ACTIVE]: 'Potwierdzona',
  [BookingStatus.CANCELLED]: 'Anulowana',
};

export const getBookingStatusLabel = (status: BookingStatus): string =>
  BOOKING_STATUS_LABELS[status];

/** Statusy rat — rodzaj żeński, bo dotyczą „raty”, a nie „planowanej transakcji”. */
export const INSTALLMENT_STATUS_LABELS: Record<
  PlannedTransactionPaymentStatus,
  string
> = {
  [PlannedTransactionPaymentStatus.PAID]: 'Opłacona',
  [PlannedTransactionPaymentStatus.AWAITING_PAYMENT]: 'Zaplanowana',
  [PlannedTransactionPaymentStatus.OVERDUE]: 'Zaległa',
  [PlannedTransactionPaymentStatus.CANCELLED]: 'Anulowana',
};

export const getInstallmentStatusLabel = (
  status: PlannedTransactionPaymentStatus,
): string => INSTALLMENT_STATUS_LABELS[status];

/**
 * Tony rat na ekranie operatora. Rozjazd z
 * `PLANNED_TRANSACTION_PAYMENT_STATUS_TONE` jest świadomy: klient widzi
 * „Oczekuje” na bursztynie jako wezwanie do zapłaty, operatorowi zaplanowana
 * rata nic nie zleca, więc stoi szaro.
 */
export const INSTALLMENT_STATUS_TONE: Record<
  PlannedTransactionPaymentStatus,
  StatusTone
> = {
  [PlannedTransactionPaymentStatus.PAID]: 'success',
  [PlannedTransactionPaymentStatus.AWAITING_PAYMENT]: 'neutral',
  [PlannedTransactionPaymentStatus.OVERDUE]: 'error',
  [PlannedTransactionPaymentStatus.CANCELLED]: 'neutral',
};

/** Etykiety przycisku akcji w wierszu sekcji. */
export const BILL_ROW_ACTION_LABELS: Record<BillRowAction, string> = {
  [BillRowAction.CHANGE]: 'Zmień',
  [BillRowAction.EDIT]: 'Edytuj',
  [BillRowAction.ADD]: 'Dodaj',
  [BillRowAction.OPEN]: 'Otwórz',
};

export const getBillRowActionLabel = (action: BillRowAction): string =>
  BILL_ROW_ACTION_LABELS[action];

/**
 * Klient rezerwacji jest w tym module jej zapisującym — rolę nadaje kontekst
 * rezerwacji, a nie kontrakt, bo ten sam człowiek na innej rezerwacji bywa
 * kimś innym.
 */
export const PERSON_KIND_LABELS: Record<PersonTargetType, string> = {
  [TargetType.PARTICIPANT]: 'Uczestnik',
  [TargetType.CLIENT]: 'Zapisujący',
};

export const getPersonKindLabel = (kind: PersonTargetType): string =>
  PERSON_KIND_LABELS[kind];

/**
 * Dopisek roli zapisującego. Zapisujący jest zawsze płatnikiem rezerwacji,
 * więc napis „Zapisujący · płatnik” powstaje wyłącznie z tej stałej.
 */
export const CLIENT_ROLE_DETAIL = 'płatnik';

interface PersonIdentityInput {
  id: string;
  kind: PersonTargetType;
  firstName: string;
  lastName: string;
  /** Dopisek po kropce: „płatnik”, „6 lat”. Brak zostawia samą rolę. */
  roleDetail?: string | null;
}

/**
 * Tożsamość osoby dla kafli, awatarów i nagłówków ekranów.
 *
 * Jedyne miejsce, w którym powstają inicjały, pełna nazwa i etykieta roli —
 * mappery rachunku, szczegółu paczki i panelu bocznego wołają tę funkcję,
 * żeby „Zapisujący · płatnik” nie był przepisywany przy każdym z nich.
 */
export const buildPersonIdentity = ({
  id,
  kind,
  firstName,
  lastName,
  roleDetail,
}: PersonIdentityInput): PersonIdentityVm => ({
  id,
  kind,
  initials: initialsOf(firstName, lastName),
  fullName: fullNameOf(firstName, lastName),
  roleLabel: roleDetail
    ? `${PERSON_KIND_LABELS[kind]} · ${roleDetail}`
    : PERSON_KIND_LABELS[kind],
});
